import React from 'react';
import { Badge } from './ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { Sparkles, Zap } from 'lucide-react';

// Feb 2026 — Auto-Evidence has 8 explicit categories. The badge keys
// off `auto_evidence.category` (set by the matcher + sentinel
// post-processor) — falling back to `auto_evidence.status` so legacy
// controls written before the rewrite still render correctly.
//
// Wording is plain and direct per user spec (option 2a):
//   • Auto-satisfied
//   • Policy violation
//   • Need more evidence
//   • Collector error
//   • {GitHub|Google Workspace} not connected
//   • Collector unavailable
//   • Manual evidence required
//   • Not applicable
const categoryMeta = {
  auto_satisfied: {
    label: 'Auto-satisfied',
    className: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    icon: '✓',
    headline: 'Auto-satisfied',
  },
  auto_partial: {
    label: 'Partial',
    className: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: '◐',
    headline: 'Partially auto-satisfied',
  },
  policy_violation: {
    label: 'Policy violation',
    className: 'bg-red-100 text-red-800 border-red-300',
    icon: '✕',
    headline: 'Policy violation',
  },
  need_more_evidence: {
    label: 'Need more evidence',
    className: 'bg-orange-100 text-orange-800 border-orange-300',
    icon: '⚠',
    headline: 'Need more evidence',
  },
  collector_error: {
    label: 'Collector error',
    className: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: '↻',
    headline: 'Collector error',
  },
  integration_disconnected: {
    label: 'Integration not connected',
    className: 'bg-sky-100 text-sky-800 border-sky-300',
    icon: '→',
    headline: 'Integration not connected',
  },
  collector_unavailable: {
    label: 'Collector unavailable',
    className: 'bg-slate-100 text-slate-700 border-slate-300',
    icon: '↑',
    headline: 'Collector unavailable',
  },
  manual_evidence_required: {
    label: 'Manual evidence required',
    className: 'bg-slate-50 text-slate-700 border-slate-300',
    icon: '↑',
    headline: 'Manual evidence required',
  },
  not_applicable: {
    label: 'Not applicable',
    className: 'bg-slate-100 text-slate-600 border-slate-300',
    icon: '—',
    headline: 'Not applicable',
  },
};

// Specialise the badge label when we know the integration's display name.
const integrationDisplayName = (source) => {
  if (!source) return null;
  if (source === 'github') return 'GitHub';
  if (source === 'google_workspace') return 'Google Workspace';
  return source;
};

// Legacy `status` → category, mirrors backend `_category_for_status`.
const statusToCategory = (status) => ({
  pass: 'auto_satisfied',
  fail: 'policy_violation',
  partial: 'policy_violation',
  insufficient_evidence: 'need_more_evidence',
  not_applicable: 'not_applicable',
}[(status || '').toLowerCase()] || 'need_more_evidence');

/**
 * Compact badge with detailed hover card for a control's auto_evidence result.
 *
 * Always renders SOMETHING so callers don't need a `—` fallback:
 *   • Verdict badge (pass/fail/partial/insufficient_evidence/not_applicable)
 *   • "Manual" chip when `control.type !== 'automated'`
 *   • "Not evaluated" chip when the control is automated but has no verdict yet
 */
export const AutoEvidenceBadge = ({ control, size = 'xs', className = '' }) => {
  const ae = control?.auto_evidence;
  const textSize = size === 'sm' ? 'text-sm' : 'text-xs';

  // Manual controls: the auto-evaluator was never going to look at them.
  if (control?.type !== 'automated') {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-500 ${textSize} ${className}`}
        title="This control is manual — Auto-Evidence does not apply. Attach evidence by hand."
        data-testid={`auto-evidence-manual-${control.id}`}
      >
        Manual
      </span>
    );
  }

  // Automated but no verdict yet — biggest workflow signal: "run the evaluator".
  if (!ae || (!ae.status && !ae.category)) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-dashed border-slate-300 bg-white text-slate-500 ${textSize} ${className}`}
        title="Automated control with no Auto-Evidence verdict yet. Click 'Auto-Evidence' to evaluate."
        data-testid={`auto-evidence-not-evaluated-${control.id}`}
      >
        Not evaluated
      </span>
    );
  }

  // Prefer the new `category` field (always set by Feb 2026 rewrite),
  // fall back to deriving from the legacy `status` for pre-rewrite rows.
  const rawCategory = ae.category || statusToCategory(ae.status);
  const meta = { ...(categoryMeta[rawCategory] || categoryMeta.need_more_evidence) };

  // For `integration_disconnected` we know which integration is missing;
  // bake the display name into the label so the user doesn't have to
  // open the hover-card to see whether it's GH or GW.
  if (rawCategory === 'integration_disconnected') {
    const sourceName = integrationDisplayName(ae.source);
    if (sourceName) {
      meta.label = `${sourceName} not connected`;
      meta.headline = `${sourceName} not connected`;
    }
  }
  const matches = ae.matches || [];
  const passCount = matches.reduce((sum, m) => sum + (m.pass_repos?.length || 0), 0);
  const failCount = matches.reduce((sum, m) => sum + (m.fail_repos?.length || 0), 0);
  const totalRepos = matches.reduce((max, m) => Math.max(max, m.total_repos || 0), 0);

  const severityDotClass = {
    critical: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-amber-500',
    low: 'bg-slate-400',
    informational: 'bg-emerald-500',
  }[ae.severity];

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span
          className={`inline-flex items-center gap-1.5 cursor-help ${className}`}
          data-testid={`auto-evidence-badge-${control.id}`}
        >
          {severityDotClass && (
            <span
              className={`inline-block w-2 h-2 rounded-full ${severityDotClass}`}
              aria-label={`severity ${ae.severity}`}
              data-testid={`inherited-severity-dot-${control.id}`}
              title={`Severity: ${ae.severity}`}
            />
          )}
          <Badge
            variant="outline"
            className={`${meta.className} ${textSize} font-medium whitespace-nowrap`}
          >
            <Zap className="w-3 h-3 mr-1" />
            <span className="mr-1">{meta.icon}</span>
            {meta.label}
            {ae.ai_fallback && (
              <Sparkles className="w-3 h-3 ml-1 text-violet-600" />
            )}
          </Badge>
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-96 p-0 overflow-hidden"
        data-testid={`auto-evidence-hover-${control.id}`}
      >
        <div className={`px-4 py-2 border-b ${meta.className} font-semibold text-sm flex items-center justify-between`}>
          <span>
            <span className="mr-2">{meta.icon}</span>
            {meta.headline}
          </span>
          {ae.ai_fallback && (
            <span className="inline-flex items-center text-[11px] font-normal text-violet-700 bg-white/70 px-1.5 py-0.5 rounded">
              <Sparkles className="w-3 h-3 mr-1" /> AI-matched
            </span>
          )}
        </div>

        <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
          {ae.reason && (
            <div>
              <p className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-1">
                Why
              </p>
              <p className="text-xs text-slate-800 leading-relaxed">{ae.reason}</p>
            </div>
          )}

          {ae.proof && (
            <div>
              <p className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-1">
                Why {ae.status === 'pass' ? 'it passed' : ae.status === 'fail' ? 'it failed' : 'this verdict'}
              </p>
              <p className="text-xs text-slate-800 leading-relaxed">{ae.proof}</p>
            </div>
          )}

          {(passCount > 0 || failCount > 0 || totalRepos > 0) && (
            <div className="flex items-center gap-3 text-xs text-slate-600">
              {passCount > 0 && (
                <span className="text-emerald-700">✓ {passCount} pass</span>
              )}
              {failCount > 0 && (
                <span className="text-red-700">✕ {failCount} fail</span>
              )}
              {totalRepos > 0 && (
                <span className="text-slate-500">of {totalRepos} repos</span>
              )}
            </div>
          )}

          {ae.what_would_fix && (
            <div className="rounded border border-orange-200 bg-orange-50 p-2.5">
              <p className="text-[11px] uppercase tracking-wide font-medium text-orange-700 mb-1">
                How to fix this
              </p>
              <p className="text-xs text-slate-800 leading-relaxed">{ae.what_would_fix}</p>
            </div>
          )}

          {ae.severity && (
            <div className="rounded border border-slate-200 bg-slate-50 p-2.5"
                 data-testid={`severity-trail-${control.id}`}>
              <p className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-1">
                Severity ({ae.severity}{ae.base_severity && ae.base_severity !== ae.severity ? ` · base ${ae.base_severity}` : ''})
              </p>
              <p className="text-xs text-slate-700 leading-relaxed">
                {ae.severity_reasoning || 'Base severity from catalog'}
              </p>
            </div>
          )}

          {ae.ai_reasoning && !ae.proof?.includes(ae.ai_reasoning) && (
            <div>
              <p className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-1">
                AI reasoning
              </p>
              <p className="text-xs italic text-slate-600 leading-relaxed">{ae.ai_reasoning}</p>
            </div>
          )}

          {matches.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-[11px] uppercase tracking-wide font-medium text-slate-500">
                Evidence signals ({matches.length})
              </summary>
              <ul className="mt-1.5 space-y-1.5">
                {matches.slice(0, 6).map((m, i) => (
                  <li key={i} className="rounded bg-slate-50 px-2 py-1.5 text-[11px] text-slate-700">
                    <div className="flex justify-between">
                      <span className="font-mono">{m.check_id}</span>
                      {m.status && (
                        <span className={categoryMeta[statusToCategory(m.status)]?.className || 'text-slate-500'}>
                          {categoryMeta[statusToCategory(m.status)]?.icon || m.status}
                        </span>
                      )}
                    </div>
                    {m.fail_repos?.length > 0 && (
                      <div className="mt-0.5 text-red-700">
                        Failing: {m.fail_repos.slice(0, 3).join(', ')}
                        {m.fail_repos.length > 3 && ` +${m.fail_repos.length - 3} more`}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="pt-2 border-t border-slate-200 text-[10px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
            {ae.rule_key && (
              <span>
                Rule: <code className="bg-slate-100 px-1 rounded">{ae.rule_key}</code>
              </span>
            )}
            {ae.evaluated_at && (
              <span>Evaluated: {new Date(ae.evaluated_at).toLocaleString()}</span>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export default AutoEvidenceBadge;
